import { useState, useRef, useEffect } from "preact/hooks";
import { ref, uploadBytesResumable } from "firebase/storage";
import { AnalyticsEvents, storySubmissionEventDefaults } from "@murmurations/core";
import { auth, storage } from "../../firebase/init";
import { registerUser, startFreshSubmissionSession } from "../../api/storiesClient";
import { trackAnalyticsEvent } from "../../analytics";
import { RecorderControls } from "./RecorderControls";
import { SubmissionForm, type SubmitChoice } from "./SubmissionForm";
import "./record.scss";

type Step = "record" | "review" | "uploading" | "success" | "error";

// ─────────────────────────────────────────────────────────────────────────────
// Voice record / submission section. Owned by [RecordSheet] inside
// [MicrositeShell]. State machine and copy match Figma frames
// 1511/3069 (intro) → 1637/3183 (recording) → 1769/3303 (review) →
// 1916/3438 (uploading) → 2040/3550 (success).
//
//
// Upload path mirrors the canonical frontend/src/pages/Upload.tsx:
//   stories/{uid}/{timestamp}_anonymous_story.{ext}
// The onStoryUploaded Cloud Function creates the Firestore doc automatically
// on Storage upload — no explicit POST /api/stories call needed.
// ─────────────────────────────────────────────────────────────────────────────

export function RecordSection() {
  const [step, setStep] = useState<Step>("record");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [deletionCode, setDeletionCode] = useState<string | null>(null);
  const [submittedWithEmail, setSubmittedWithEmail] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Phone recording state (frame 3183) trims the intro down to just the
  // "Press the button below..." line; desktop (1637) keeps all three.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(pointer: coarse)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  const fakeProgressRef = useRef(0);
  const fakeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearFakeTimer = () => {
    if (fakeTimerRef.current) {
      clearInterval(fakeTimerRef.current);
      fakeTimerRef.current = null;
    }
  };

  const handleRecordingComplete = (blob: Blob) => {
    setAudioBlob(blob);
    setStep("review");
  };

  const handleReRecord = () => {
    setAudioBlob(null);
    setStep("record");
  };

  const handleSubmit = async ({ choice, email }: { choice: SubmitChoice; email?: string }) => {
    if (!audioBlob) return;

    setStep("uploading");
    setProgress(0);
    fakeProgressRef.current = 0;

    fakeTimerRef.current = setInterval(() => {
      if (fakeProgressRef.current < 85) {
        fakeProgressRef.current = Math.min(fakeProgressRef.current + Math.random() * 3 + 1, 85);
        setProgress(fakeProgressRef.current);
      }
    }, 400);

    try {
      // Mint a fresh anonymous identity for THIS submission. The submitter email
      // in the CMS is resolved by `users where uids array-contains submittedByUid`,
      // so a reused UID that was ever tied to an email keeps surfacing that stale
      // address on every later story (anonymous or otherwise). A per-submission UID
      // maps to exactly the email chosen here — or to none for anonymous.
      const idToken = await startFreshSubmissionSession();
      const user = auth.currentUser!;

      if (choice === "email" && email) {
        try {
          await registerUser(idToken, email);
        } catch (err) {
          console.error("[RecordSection] Failed to associate email:", err);
        }
      }

      const code = crypto.randomUUID();

      const mimeType = audioBlob.type || "audio/webm";
      const ext = (mimeType.split("/")[1] ?? "webm").split(";")[0];
      const storagePath = `stories/${user.uid}/${Date.now()}_anonymous_story.${ext}`;
      const storageRef = ref(storage, storagePath);

      const uploadMetadata = {
        contentType: mimeType,
        customMetadata: {
          isAnonymous: choice === "anon" ? "true" : "false",
          deletionCode: code,
        },
      };
      const uploadTask = uploadBytesResumable(storageRef, audioBlob, uploadMetadata);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snap) => {
            const realProgress = (snap.bytesTransferred / snap.totalBytes) * 100;
            if (realProgress > fakeProgressRef.current) {
              fakeProgressRef.current = realProgress;
              setProgress(realProgress);
            }
          },
          reject,
          () => resolve(),
        );
      });

      clearFakeTimer();
      setProgress(100);
      await new Promise<void>((r) => setTimeout(r, 400));

      trackAnalyticsEvent(AnalyticsEvents.SUBMIT_FORM, {
        ...storySubmissionEventDefaults,
        submission_status: "success",
      });
      setDeletionCode(code ?? null);
      setSubmittedWithEmail(choice === "email");
      setStep("success");
    } catch (err) {
      clearFakeTimer();
      console.error("[RecordSection] Upload failed:", err);
      trackAnalyticsEvent(AnalyticsEvents.SUBMIT_FORM, {
        ...storySubmissionEventDefaults,
        submission_status: "error",
      });
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setStep("error");
    }
  };

  // ── Steps ─────────────────────────────────────────────────────────────────
  // Styling lives in record.scss (.rec-* classes); only the dynamic progress
  // width stays inline.

  if (step === "record") {
    const trimIntro = isRecording && isMobile;
    return (
      <div className="rec-section">
        <h2 className="rec-heading">Share your story</h2>
        {!trimIntro && (
          <>
            <p className="rec-sub">How has the presence of ICE affected you, your family, or your community?</p>
            <p className="rec-sub">Share a story, feeling, or experience — and your hopes for the future.</p>
          </>
        )}
        <p className="rec-sub rec-sub--lead-out">
          Press the button below, then speak. Your recording will be reviewed by moderators before appearing in the murmuration.
        </p>
        <RecorderControls onRecordingComplete={handleRecordingComplete} onRecordingChange={setIsRecording} />
      </div>
    );
  }

  if (step === "review" && audioBlob) {
    return (
      <div className="rec-section">
        <h2 className="rec-heading">Submit your story</h2>
        <p className="rec-sub">Listen back, then choose how you'd like to submit.</p>
        <SubmissionForm audioBlob={audioBlob} onSubmit={handleSubmit} onReRecord={handleReRecord} />
      </div>
    );
  }

  if (step === "uploading") {
    return (
      <div className="rec-section">
        <h2 className="rec-heading">Uploading</h2>
        <p className="rec-sub rec-sub--tight">{Math.round(progress)}%</p>
        <div className="rec-progress">
          <div className="rec-progress__fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="rec-section">
        <h2 className="rec-heading">Story submitted. Thank you.</h2>
        <p className="rec-sub">
          Your story will be reviewed by our team. To protect your anonymity while still allowing you to remove your story in the future, we've generated a
          unique deletion code. Please save it somewhere safe — it's the only way to delete your story later.
        </p>
        {submittedWithEmail && <p className="rec-sub">Once approved and live, we'll send you a link directly to your bird in the murmuration.</p>}
        {deletionCode && (
          <div className="rec-code-box">
            <code className="rec-code">{deletionCode}</code>
            <button onClick={() => navigator.clipboard?.writeText(deletionCode)} title="Copy deletion code" className="rec-copy-btn">
              Copy
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true">
                <rect x="7" y="7" width="10" height="10" rx="1.5" />
                <path d="M13 7V4.5A1.5 1.5 0 0 0 11.5 3h-7A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13H7" />
              </svg>
            </button>
          </div>
        )}
        <button
          onClick={() => {
            setStep("record");
            setAudioBlob(null);
            setDeletionCode(null);
            setSubmittedWithEmail(false);
          }}
          className="rec-ghost-btn"
        >
          Record another story
        </button>
      </div>
    );
  }

  // error step
  return (
    <div className="rec-section">
      <h2 className="rec-heading">Something went wrong</h2>
      <p className="rec-error-text">{errorMsg}</p>
      <button onClick={() => setStep("review")} className="rec-ghost-btn">
        Try again
      </button>
    </div>
  );
}
